/**
 * Mestti OPS client — mint RS256 in memory + fetch SSE (never EventSource).
 * Token must never enter localStorage, query string, logs, or test exports.
 *
 * Per-run Bearer lives in runBearerByRunId. stream/cancel never mint mid-run.
 */
import { API_BASE_URL, getAiBaseUrl } from '../../../config/api';
import secureHttpService from '../../../services/secureHttpService';
import {
  clearConsultoriaCredential,
  clearConsultoriaRun,
  consultoriaActiveRunCount,
  getBoundConsultoriaIdentityKey,
  getConsultoriaLastEventId,
  getConsultoriaMemoryCredential,
  getConsultoriaRunBearer,
  getConsultoriaSessionEpoch,
  parseConsultoriaScopeString,
  registerConsultoriaRunTeardown,
  setConsultoriaLastEventId,
  setConsultoriaMemoryCredential,
  setConsultoriaRunBearer,
} from './consultoriaSession';
import {
  CONSULTORIA_CHAT_START_TIMEOUT_MS,
  CONSULTORIA_SSE_IDLE_TIMEOUT_MS,
} from '../constants/runtimeLimits';

export {
  bindConsultoriaIdentity,
  clearConsultoriaCredential,
  clearConsultoriaRun,
  clearConsultoriaSession,
  consultoriaIdentityKey,
  getConsultoriaScopes,
  getConsultoriaSessionEpoch,
  hasConsultoriaScope,
} from './consultoriaSession';

registerConsultoriaRunTeardown((bearers) => {
  const base = getAiBaseUrl();
  bearers.forEach((token, runId) => {
    void fetch(`${base}/runs/${runId}/cancel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
    }).catch(() => undefined);
  });
});

export class ConsultoriaHttpError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ConsultoriaHttpError';
    this.status = status;
  }
}

function composeAbortSignal(signals: Array<AbortSignal | undefined>): AbortSignal | undefined {
  const active = signals.filter((s): s is AbortSignal => Boolean(s));
  if (!active.length) return undefined;
  // Avoid AbortSignal.any — not in our TS lib DOM typings / older browsers.
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  for (const signal of active) {
    if (signal.aborted) {
      controller.abort();
      return controller.signal;
    }
    signal.addEventListener('abort', onAbort, { once: true });
  }
  return controller.signal;
}

function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  window.setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

export interface ConsultoriaToken {
  access_token: string;
  token_type: string;
  expires_in: number;
  kid: string;
  scope: string;
  jti: string;
}

export interface MemoryProposalPayload {
  proposal_id: string;
  content: string;
  kind: string;
  scope: string;
  status: 'pending' | 'approving' | 'approved' | 'rejected' | 'failed';
}

export function parseMemoryProposal(value: unknown): MemoryProposalPayload | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const obj = value as Record<string, unknown>;
  if (typeof obj.proposal_id !== 'string' || !obj.proposal_id) return undefined;
  if (typeof obj.content !== 'string') return undefined;
  
  const status = typeof obj.status === 'string' ? obj.status : 'pending';
  const validStatus = ['pending', 'approving', 'approved', 'rejected', 'failed'].includes(status) 
    ? (status as MemoryProposalPayload['status']) 
    : 'pending';

  return {
    proposal_id: obj.proposal_id,
    content: obj.content,
    kind: typeof obj.kind === 'string' ? obj.kind : 'preference',
    scope: typeof obj.scope === 'string' ? obj.scope : 'user',
    status: validStatus,
  };
}

export interface EvidencePayload {
  source?: string;
  /** User-friendly labels of tools/consultas used (never raw get_* ids). */
  tools_used?: string[];
  period_start?: string;
  period_end?: string;
  timezone?: string;
  coverage?: string;
  freshness?: string | null;
  formula_version?: string;
  metric_definition?: string;
  insufficient_data?: boolean;
  truncated?: boolean;
  is_fake_fixture?: boolean;
  freshness_limited?: boolean;
  proposed_memory?: MemoryProposalPayload;
}

export interface ConsultoriaSseEvent {
  seq?: number;
  event_type?: string;
  type?: string;
  payload?: Record<string, unknown>;
  data?: Record<string, unknown>;
}

export function isConsultoriaEnabled(): boolean {
  const raw = process.env.REACT_APP_CONSULTORIA_ENABLED;
  if (raw === undefined || raw === '') {
    return true;
  }
  return String(raw).toLowerCase() === 'true';
}

/** @deprecated use clearConsultoriaRun */
export function clearConsultoriaRunCursor(runId?: string): void {
  clearConsultoriaRun(runId);
}

/**
 * A chat run lasts up to the AI's wall clock (240 s) and uses the bearer it started
 * with; the backend rejects it 30 s after expiry. Reusing a cached token with only
 * seconds left made runs die mid-way ("credencial expirada na tool", production
 * 2026-09-26). A new run therefore needs a token that outlives it; other calls are
 * short and keep the 15 s margin. If the backend mints shorter tokens, every run
 * start simply mints a fresh one.
 */
export const RUN_TOKEN_MIN_VALIDITY_MS = 270_000;
const DEFAULT_TOKEN_MIN_VALIDITY_MS = 15_000;

async function mintSessionBearer(
  minValidityMs: number = DEFAULT_TOKEN_MIN_VALIDITY_MS,
): Promise<string> {
  const boundIdentityKey = getBoundConsultoriaIdentityKey();
  if (!boundIdentityKey) {
    throw new Error('Mint Otto falhou: identidade ausente');
  }
  const epochAtStart = getConsultoriaSessionEpoch();
  const now = Date.now();
  const memoryCredential = getConsultoriaMemoryCredential();
  if (
    memoryCredential &&
    memoryCredential.identityKey === boundIdentityKey &&
    memoryCredential.expiresAtMs > now + minValidityMs
  ) {
    return memoryCredential.token;
  }
  const response = await secureHttpService
    .post<ConsultoriaToken>('/consultoria/token')
    .catch((error: unknown) => {
      const status = Number(
        (error as { response?: { status?: number } } | undefined)?.response?.status,
      );
      if (status === 403) {
        throw new ConsultoriaHttpError(403, 'Acesso ao Otto não autorizado');
      }
      throw error;
    });
  if (
    getConsultoriaSessionEpoch() !== epochAtStart ||
    getBoundConsultoriaIdentityKey() !== boundIdentityKey
  ) {
    throw new Error('Mint Otto cancelado: sessão alterada');
  }
  const body = response.data;
  if (!body?.access_token) {
    throw new Error('Mint Otto falhou: token ausente');
  }
  setConsultoriaMemoryCredential({
    token: body.access_token,
    expiresAtMs: now + Math.max(30, Number(body.expires_in) || 300) * 1000,
    identityKey: boundIdentityKey,
    scopes: parseConsultoriaScopeString(body.scope),
  });
  return body.access_token;
}

function requireRunBearer(runId: string): string {
  const token = getConsultoriaRunBearer(runId);
  if (!token) {
    throw new Error('credencial do run ausente');
  }
  return token;
}

function endRunWithoutRefresh(runId: string): void {
  clearConsultoriaRun(runId);
}

/**
 * Mint do bearer de sessão para consumidores fora do chat (ex.: módulo
 * "Agente IA"). Reaproveita o cache em memória e o vínculo de identidade —
 * o token nunca vai para localStorage, query string ou log.
 */
export async function mintConsultoriaBearer(): Promise<string> {
  return mintSessionBearer();
}

export async function startConsultoriaChat(
  content: string,
  options?: {
    threadId?: string;
    mode?: 'conversation' | 'faq' | 'agent';
    signal?: AbortSignal;
  },
): Promise<{ runId: string }> {
  const token = await mintSessionBearer(RUN_TOKEN_MIN_VALIDITY_MS);
  const base = getAiBaseUrl();
  const signal = composeAbortSignal([
    options?.signal,
    timeoutSignal(CONSULTORIA_CHAT_START_TIMEOUT_MS),
  ]);
  const res = await fetch(`${base}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      content,
      thread_id: options?.threadId,
      mode: options?.mode || 'conversation',
    }),
    signal,
    credentials: 'omit',
  });
  if (res.status === 401) {
    // 401 encerra sem refresh
    clearConsultoriaCredential();
    throw new ConsultoriaHttpError(401, 'chat HTTP 401');
  }
  if (!res.ok) {
    throw new ConsultoriaHttpError(res.status, `chat HTTP ${res.status}`);
  }
  const data = (await res.json()) as { run_id: string };
  const runId = data.run_id;
  clearConsultoriaRun(runId);
  setConsultoriaRunBearer(runId, token);
  return { runId };
}

/**
 * Parse SSE buffer into complete frames.
 * Keeps `rest` as an unmodified suffix of the original buffer so a trailing
 * `\r` waiting for the next chunk's `\n` is never prematurely normalized.
 */
export function parseSseChunk(
  buffer: string,
): { events: Array<{ id?: string; data: string }>; rest: string } {
  const events: Array<{ id?: string; data: string }> = [];
  let start = 0;
  let i = 0;
  while (i < buffer.length) {
    let sepLen = 0;
    if (buffer.startsWith('\r\n\r\n', i)) {
      sepLen = 4;
    } else if (buffer[i] === '\n' && buffer[i + 1] === '\n') {
      sepLen = 2;
    }
    if (sepLen === 0) {
      i += 1;
      continue;
    }
    const rawFrame = buffer.slice(start, i);
    i += sepLen;
    start = i;
    if (!rawFrame.trim()) continue;
    const frameLines = rawFrame.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    let id: string | undefined;
    const dataLines: string[] = [];
    for (const line of frameLines) {
      if (line.startsWith('id:')) {
        id = line.slice(3).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    if (dataLines.length) {
      events.push({ id, data: dataLines.join('\n') });
    }
  }
  return { events, rest: buffer.slice(start) };
}

export type StreamOptions = {
  signal?: AbortSignal;
  lastEventId?: string;
  maxReconnects?: number;
  idleTimeoutMs?: number;
  onMalformedFrame?: (reason: string) => void;
};

export async function* streamConsultoriaRunEvents(
  runId: string,
  options?: StreamOptions,
): AsyncGenerator<ConsultoriaSseEvent, void, unknown> {
  const base = getAiBaseUrl();
  const maxReconnects = options?.maxReconnects ?? 2;
  const idleTimeoutMs = options?.idleTimeoutMs ?? CONSULTORIA_SSE_IDLE_TIMEOUT_MS;
  let reconnects = 0;
  let cursor = options?.lastEventId ?? getConsultoriaLastEventId(runId) ?? undefined;
  // Freeze bearer for the whole run — never mint/refresh here.
  const token = requireRunBearer(runId);

  try {
    while (true) {
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      };
      if (cursor) {
        headers['Last-Event-ID'] = cursor;
      }
      let res: Response;
      try {
        res = await fetch(`${base}/runs/${runId}/events`, {
          method: 'GET',
          headers,
          signal: options?.signal,
          credentials: 'omit',
        });
      } catch (err) {
        if (options?.signal?.aborted) {
          endRunWithoutRefresh(runId);
          return;
        }
        if (reconnects < maxReconnects) {
          reconnects += 1;
          continue;
        }
        endRunWithoutRefresh(runId);
        throw err;
      }
      if (res.status === 401) {
        // 401 encerra sem refresh
        clearConsultoriaCredential();
        endRunWithoutRefresh(runId);
        throw new ConsultoriaHttpError(401, 'SSE HTTP 401');
      }
      if (!res.ok || !res.body) {
        if (res.status >= 500 && reconnects < maxReconnects) {
          reconnects += 1;
          continue;
        }
        endRunWithoutRefresh(runId);
        throw new ConsultoriaHttpError(res.status, `SSE HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let sawDone = false;
      while (true) {
        let idleTimer: ReturnType<typeof setTimeout> | undefined;
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await Promise.race([
            reader.read(),
            new Promise<never>((_, reject) => {
              idleTimer = setTimeout(() => {
                void reader.cancel().catch(() => undefined);
                reject(new Error('SSE idle timeout'));
              }, idleTimeoutMs);
            }),
          ]);
        } finally {
          if (idleTimer) clearTimeout(idleTimer);
        }
        const { done, value } = chunk;
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSseChunk(buffer);
        buffer = parsed.rest;
        for (const frame of parsed.events) {
          if (frame.id) {
            cursor = frame.id;
            setConsultoriaLastEventId(runId, frame.id);
          }
          if (frame.data === '[DONE]') {
            sawDone = true;
            endRunWithoutRefresh(runId);
            return;
          }
          try {
            const event = JSON.parse(frame.data) as ConsultoriaSseEvent;
            const eventType = String(event.event_type || event.type || '');
            // Only end the stream on run_finished. An earlier `error` may be
            // followed by a degraded `answer` — keep reading until finished.
            yield event;
            if (eventType === 'run_finished') {
              endRunWithoutRefresh(runId);
              return;
            }
          } catch {
            options?.onMalformedFrame?.('json_parse');
            endRunWithoutRefresh(runId);
            throw new Error('SSE frame malformado');
          }
        }
      }
      if (sawDone) return;
      if (reconnects < maxReconnects && !options?.signal?.aborted) {
        reconnects += 1;
        continue;
      }
      endRunWithoutRefresh(runId);
      return;
    }
  } catch (err) {
    if (getConsultoriaRunBearer(runId)) {
      endRunWithoutRefresh(runId);
    }
    throw err;
  }
}

export async function cancelConsultoriaRun(runId: string): Promise<void> {
  const token = getConsultoriaRunBearer(runId);
  if (!token) {
    clearConsultoriaRun(runId);
    return;
  }
  const base = getAiBaseUrl();
  try {
    const res = await fetch(`${base}/runs/${runId}/cancel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
    });
    if (res.status === 401) {
      clearConsultoriaCredential();
    }
  } finally {
    endRunWithoutRefresh(runId);
  }
}

export function consultoriaDebugInfo(): {
  apiBase: string;
  aiBase: string;
  hasToken: boolean;
  activeRuns: number;
  identityKey: string | null;
} {
  const memoryCredential = getConsultoriaMemoryCredential();
  return {
    apiBase: API_BASE_URL,
    aiBase: getAiBaseUrl(),
    hasToken: Boolean(memoryCredential?.token) || consultoriaActiveRunCount() > 0,
    activeRuns: consultoriaActiveRunCount(),
    identityKey: getBoundConsultoriaIdentityKey(),
  };
}

export interface ConsultoriaThread {
  thread_id: string;
  title: string;
  summary: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ProposalPayload {
  proposal_id: string;
  action_type: string;
  proposal_hash: string;
  preview?: Record<string, unknown>;
  reason?: string;
}

export async function listConsultoriaThreads(options?: {
  signal?: AbortSignal;
}): Promise<ConsultoriaThread[]> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const res = await fetch(`${base}/threads`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit',
    signal: options?.signal,
  });
  if (!res.ok) throw new Error(`threads HTTP ${res.status}`);
  return (await res.json()) as ConsultoriaThread[];
}

export async function deleteConsultoriaThread(threadId: string): Promise<void> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const res = await fetch(`${base}/threads/${threadId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit',
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(`delete thread HTTP ${res.status}`);
  }
}

export async function submitConsultoriaFeedback(input: {
  assistantMessageId: string;
  runId: string;
  rating: 'like' | 'dislike' | 'clear';
  categories?: string[];
  comment?: string;
}): Promise<{ feedback_id: string | null; rating: string | null; categories: string[] }> {
  const token = await mintSessionBearer();
  const res = await fetch(`${getAiBaseUrl()}/feedback`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      assistant_message_id: input.assistantMessageId,
      run_id: input.runId,
      rating: input.rating,
      categories: input.categories || [],
      comment: input.comment || null,
    }),
  });
  if (!res.ok) {
    throw new Error(`feedback HTTP ${res.status}`);
  }
  return (await res.json()) as {
    feedback_id: string | null;
    rating: string | null;
    categories: string[];
  };
}

export async function loadConsultoriaThreadMessages(
  threadId: string,
): Promise<
  Array<{
    message_id?: string;
    role: string;
    content: string;
    created_at?: string | null;
    metadata?: Record<string, unknown> | null;
    feedback?: { rating?: string; categories?: string[] } | null;
  }>
> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const res = await fetch(`${base}/threads/${threadId}/messages`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit',
  });
  if (!res.ok) throw new Error(`messages HTTP ${res.status}`);
  const rows = (await res.json()) as Array<{
    message_id?: string;
    role: string;
    content: string;
    created_at?: string | null;
    metadata?: Record<string, unknown> | null;
    feedback?: { rating?: string; categories?: string[] } | null;
  }>;
  return rows.map((r) => ({
    message_id: r.message_id ? String(r.message_id) : undefined,
    role: r.role,
    content: r.content,
    created_at: r.created_at,
    metadata: r.metadata ?? null,
    feedback: r.feedback ?? null,
  }));
}

export async function approveConsultoriaProposal(
  proposalId: string,
  proposalHash: string,
): Promise<Record<string, unknown>> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const res = await fetch(`${base}/proposals/${proposalId}/approve`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ proposal_hash: proposalHash }),
    credentials: 'omit',
  });
  if (!res.ok) {
    throw new ConsultoriaHttpError(res.status, `approve HTTP ${res.status}`);
  }
  return (await res.json()) as Record<string, unknown>;
}

export async function rejectConsultoriaProposal(
  proposalId: string,
  proposalHash: string,
): Promise<void> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const res = await fetch(`${base}/proposals/${proposalId}/reject`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ proposal_hash: proposalHash }),
    credentials: 'omit',
  });
  if (!res.ok) {
    throw new ConsultoriaHttpError(res.status, `reject HTTP ${res.status}`);
  }
}

export interface MemoryItem {
  id: string;
  content: string;
  kind: string;
  status: string;
  scope: string;
  created_at: string;
}

function normalizeMemoryItem(raw: Record<string, unknown>): MemoryItem {
  return {
    id: String(raw.id || raw.memory_id || ''),
    content: String(raw.content || ''),
    kind: String(raw.kind || raw.memory_type || ''),
    status: String(raw.status || 'active'),
    scope: String(raw.scope || 'user'),
    created_at: String(raw.created_at || ''),
  };
}

function parseMemoryListPayload(data: unknown): MemoryItem[] {
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { items?: unknown[] })?.items)
      ? (data as { items: unknown[] }).items
      : Array.isArray((data as { memories?: unknown[] })?.memories)
        ? (data as { memories: unknown[] }).memories
        : [];
  return rows
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map(normalizeMemoryItem)
    .filter((item) => Boolean(item.id));
}

async function memoriesFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
    credentials: 'omit',
  });
  if (res.status === 401) {
    clearConsultoriaCredential();
    throw new Error('memories HTTP 401');
  }
  return res;
}

export async function listMemories(): Promise<MemoryItem[]> {
  const res = await memoriesFetch('/memories');
  if (!res.ok) throw new Error(`memories HTTP ${res.status}`);
  return parseMemoryListPayload(await res.json());
}

export async function listProposedMemories(): Promise<MemoryItem[]> {
  const res = await memoriesFetch('/memories/proposed');
  if (!res.ok) throw new Error(`memories proposed HTTP ${res.status}`);
  return parseMemoryListPayload(await res.json());
}

export async function getMemory(id: string): Promise<MemoryProposalPayload | null> {
  const res = await memoriesFetch(`/memories/${encodeURIComponent(id)}`);
  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error(`get memory HTTP ${res.status}`);
  }
  const data = await res.json() as MemoryItem;
  
  // Convert MemoryItem to MemoryProposalPayload for the card component
  let mappedStatus: MemoryProposalPayload['status'] = 'pending';
  if (data.status === 'active') mappedStatus = 'approved';
  else if (data.status === 'rejected') mappedStatus = 'rejected';
  else if (data.status === 'proposed') mappedStatus = 'pending';
  
  return {
    proposal_id: data.id,
    content: data.content,
    kind: data.kind || 'preference',
    scope: data.scope || 'user',
    status: mappedStatus,
  };
}

export async function approveMemory(id: string): Promise<MemoryItem> {
  const res = await memoriesFetch(`/memories/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`approve memory HTTP ${res.status}`);
  // API returns 204; return a stub so callers can refresh lists.
  return {
    id,
    content: '',
    kind: '',
    status: 'active',
    scope: 'user',
    created_at: '',
  };
}

export async function rejectMemory(id: string): Promise<void> {
  const res = await memoriesFetch(`/memories/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`reject memory HTTP ${res.status}`);
}

export async function forgetMemory(id: string): Promise<void> {
  const res = await memoriesFetch(`/memories/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`forget memory HTTP ${res.status}`);
}

export interface SkillCandidateItem {
  id: string;
  name: string;
  description: string;
  content: string;
  operation: string;
  target_skill: string | null;
  reason: string;
  category: string;
  status: string;
}

export interface SkillCatalogItem {
  name: string;
  description: string;
  category: string;
  scope: 'system' | 'company' | string;
  id: string | null;
  can_delete: boolean;
}

export interface SkillCatalog {
  system: SkillCatalogItem[];
  company: SkillCatalogItem[];
}

function normalizeSkillCandidate(raw: Record<string, unknown>): SkillCandidateItem {
  return {
    id: String(raw.id || raw.candidate_id || ''),
    name: String(raw.name || ''),
    description: String(raw.description || ''),
    content: String(raw.content || ''),
    operation: String(raw.operation || 'create'),
    target_skill: raw.target_skill ? String(raw.target_skill) : null,
    reason: String(raw.reason || ''),
    category: String(raw.category || 'tenant'),
    status: String(raw.status || 'proposed'),
  };
}

function normalizeCatalogItem(raw: Record<string, unknown>): SkillCatalogItem {
  return {
    name: String(raw.name || ''),
    description: String(raw.description || ''),
    category: String(raw.category || ''),
    scope: String(raw.scope || 'system'),
    id: raw.id ? String(raw.id) : null,
    can_delete: Boolean(raw.can_delete),
  };
}

export async function listSkillsCatalog(): Promise<SkillCatalog> {
  const res = await memoriesFetch('/skills');
  if (!res.ok) throw new Error(`skills catalog HTTP ${res.status}`);
  const data = (await res.json()) as Record<string, unknown>;
  const systemRaw = Array.isArray(data.system) ? data.system : [];
  const companyRaw = Array.isArray(data.company) ? data.company : [];
  return {
    system: systemRaw
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map(normalizeCatalogItem)
      .filter((item) => Boolean(item.name)),
    company: companyRaw
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map(normalizeCatalogItem)
      .filter((item) => Boolean(item.name)),
  };
}

export async function listSkillCandidates(
  status: string = 'active',
): Promise<SkillCandidateItem[]> {
  const q = status ? `?status_filter=${encodeURIComponent(status)}` : '';
  const res = await memoriesFetch(`/skills/candidates${q}`);
  if (!res.ok) throw new Error(`skill candidates HTTP ${res.status}`);
  const data = await res.json();
  const rows = Array.isArray(data) ? data : [];
  return rows
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map(normalizeSkillCandidate)
    .filter((item) => Boolean(item.id));
}

export async function approveSkillCandidate(id: string): Promise<void> {
  const res = await memoriesFetch(`/skills/candidates/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`approve skill HTTP ${res.status}`);
}

export async function rejectSkillCandidate(id: string): Promise<void> {
  const res = await memoriesFetch(`/skills/candidates/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`reject skill HTTP ${res.status}`);
}

export async function forgetSkillCandidate(id: string): Promise<void> {
  const res = await memoriesFetch(`/skills/candidates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`forget skill HTTP ${res.status}`);
}

/** STT — multipart upload to mestti_ai Whisper proxy. */
export async function transcribeConsultoriaAudio(
  blob: Blob,
  options?: { filename?: string; signal?: AbortSignal },
): Promise<{ text: string }> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const form = new FormData();
  const name =
    options?.filename ||
    (blob.type.includes('mp4') ? 'audio.mp4' : 'audio.webm');
  form.append('file', blob, name);
  const res = await fetch(`${base}/transcribe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
    signal: options?.signal,
    credentials: 'omit',
  });
  if (res.status === 401) {
    clearConsultoriaCredential();
    throw new Error('transcribe HTTP 401');
  }
  if (res.status === 503) {
    throw new Error('AUDIO_NOT_CONFIGURED');
  }
  if (!res.ok) {
    throw new Error(`transcribe HTTP ${res.status}`);
  }
  const data = (await res.json()) as { text?: string };
  const text = String(data.text || '').trim();
  if (!text) throw new Error('transcribe empty');
  return { text };
}

/** TTS — returns a blob URL (caller should revoke). */
export async function synthesizeConsultoriaSpeech(
  text: string,
  options?: { signal?: AbortSignal },
): Promise<{ blobUrl: string; contentType: string }> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const res = await fetch(`${base}/tts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ text: text.slice(0, 4000) }),
    signal: options?.signal,
    credentials: 'omit',
  });
  if (res.status === 401) {
    clearConsultoriaCredential();
    throw new Error('tts HTTP 401');
  }
  if (res.status === 503) {
    throw new Error('AUDIO_NOT_CONFIGURED');
  }
  if (!res.ok) {
    throw new Error(`tts HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  return { blobUrl, contentType: blob.type || 'audio/mpeg' };
}

/** Voice availability for this company (GET /audio/status on the AI service). */
export async function getConsultoriaAudioStatus(options?: {
  signal?: AbortSignal;
}): Promise<{ configured: boolean; stt: boolean; tts: boolean }> {
  const token = await mintSessionBearer();
  const base = getAiBaseUrl();
  const res = await fetch(`${base}/audio/status`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    signal: options?.signal,
    credentials: 'omit',
  });
  if (res.status === 401) {
    clearConsultoriaCredential();
    throw new Error('audio status HTTP 401');
  }
  if (!res.ok) throw new Error(`audio status HTTP ${res.status}`);
  const data = (await res.json()) as { configured?: boolean; stt?: boolean; tts?: boolean };
  return {
    configured: Boolean(data.configured),
    stt: Boolean(data.stt ?? data.configured),
    tts: Boolean(data.tts ?? data.configured),
  };
}
